import { Category, Brand, ProductDetails, isCategory, isBrand, isString } from '@models';
import { Navigatable, BreadcrumbItem } from './breadcrumbs.component';
import { INavigationTree, isNavigationTree } from '@navigation';
import { APPLE_STORE_PAGES_ENABLED, HREF } from '@core';

// Apple brand ID constant - adjust this to match your actual Apple brand/category ID
const APPLE_CATEGORY_ID = 60001;

export const buildBreadcrumbsConfig = (
  segments: Array<Category | Brand | INavigationTree | ProductDetails['name']>,
  options: {
    withRootSegment?: boolean;
    rootSegmentConfig?: {
      path: string;
      label: string;
    };
  } = {},
): BreadcrumbItem[] => {
  const result: BreadcrumbItem[] = [];

  // Check if this is an Apple product breadcrumb
  const isAppleBreadcrumb = segments.some((segment) => isCategory(segment) && isAppleCategory(segment));

  if (isAppleBreadcrumb) {
    return buildAppleBreadcrumbs(segments, options);
  }

  // Original logic for non-Apple products
  const traverse = (obj: Category | Brand | INavigationTree | ProductDetails['name']): void => {
    if (!obj) {
      return;
    }

    if (isCategory(obj)) {
      result.push(buildSegmentFromCategory(obj));

      if (obj.subCategories && obj.subCategories.length > 0) {
        traverse(obj.subCategories[0]);
      }
    } else if (isBrand(obj)) {
      result.push(buildSegmentFromBrand(obj));
    } else if (isNavigationTree(obj)) {
      result.push(buildSegmentFromNavigationTree(obj));
    } else if (isString(obj)) {
      result.push(buildNotNavigatableSegment(obj));
      return;
    }
  };

  segments.forEach((segment) => traverse(segment));

  if (options.withRootSegment) {
    result.unshift(
      buildSegmentForHomePage(options.rootSegmentConfig?.label ?? '', options.rootSegmentConfig?.path ?? ''),
    );
  }

  return result;
};

const buildAppleBreadcrumbs = (
  segments: Array<Category | Brand | INavigationTree | ProductDetails['name']>,
  options: {
    withRootSegment?: boolean;
    rootSegmentConfig?: {
      path: string;
      label: string;
    };
  } = {},
): BreadcrumbItem[] => {
  const result: BreadcrumbItem[] = [];

  // Add root segment if requested
  if (options.withRootSegment) {
    result.push(buildSegmentForHomePage(options.rootSegmentConfig?.label ?? '', options.rootSegmentConfig?.path ?? ''));
  }

  // Add Apple as the brand segment
  result.push(buildAppleBrandSegment());

  // Process Apple category segments, skipping the root Apple category
  const categorySegments = segments.filter((segment) => isCategory(segment)) as Category[];
  const filteredSegments = categorySegments.filter((category) => category.id !== APPLE_CATEGORY_ID);

  filteredSegments.forEach((category) => {
    result.push(buildAppleCategorySegment(category));
  });

  // Handle any non-category segments (like product names)
  segments.forEach((segment) => {
    if (isString(segment)) {
      result.push(buildNotNavigatableSegment(segment));
    }
  });

  return result;
};

const isAppleCategory = (category: Category): boolean => {
  // Check if this category is part of the Apple hierarchy
  return category.id === APPLE_CATEGORY_ID || category.pId === APPLE_CATEGORY_ID || isDescendantOfApple(category);
};

const isDescendantOfApple = (category: Category): boolean => {
  // Check if this category is a descendant of Apple by looking at ID ranges
  // This is based on the pattern in your data where Apple subcategories have IDs in specific ranges
  return category.id >= 60001 && category.id <= 69999;
};

const buildAppleBrandSegment = (): BreadcrumbItem => ({
  name: 'Apple',
  ...buildNavigatable(`/${HREF.BRANDSTORE}/1032`, { data: [] }),
});

const buildSegmentFromCategory = (obj: Category | INavigationTree) => ({
  name: obj.name,
  ...buildNavigatable(`/${HREF.HOME}/category/${obj.id}`, { data: [obj] }),
});

// Apple categories that have dedicated Apple category pages
const APPLE_CATEGORY_PAGE_IDS = new Set([
  60002, // Mac
  60003, // iPad
  60004, // iPhone
  60005, // Watch
  60007, // AirPods
]);

const buildAppleCategorySegment = (obj: Category) => {
  // Check if this is a top-level Apple category (direct child of Apple root)
  const isTopLevelAppleCategory = obj.pId === APPLE_CATEGORY_ID;

  if (APPLE_STORE_PAGES_ENABLED && isTopLevelAppleCategory && APPLE_CATEGORY_PAGE_IDS.has(obj.id)) {
    // Use Apple category page for categories that have dedicated Apple pages
    return {
      name: obj.name,
      ...buildNavigatable(`/apple/category/${obj.id}`, { data: [obj] }),
    };
  } else {
    // Use regular category page for all other categories (including Beats, TV & Home, Accessories, etc.)
    return {
      name: obj.name,
      ...buildNavigatable(`/${HREF.CATEGORY_PRODUCTS}/${obj.id}`, { data: [obj] }),
    };
  }
};

const buildSegmentFromBrand = (obj: Brand) => ({
  name: obj.name,
  ...buildNavigatable(`/marketplace/${obj.id}`, { data: [obj] }),
});

const buildNotNavigatableSegment = (label: string) => ({
  name: label,
  ...buildNavigatable(null),
});

const buildSegmentForHomePage = (label: string, path: string) => ({
  name: label,
  ...buildNavigatable(path),
});

export const buildSegmentFromNavigationTree = (obj: INavigationTree) => ({
  name: obj.name,
  ...buildNavigatable(`/${HREF.HOME}/${obj.id}`, { data: [obj] }),
});

export const buildNavigatable = (
  path: Navigatable<Object>['path'],
  params: Navigatable<Object>['params'] = {},
): Navigatable<Object> => ({ path, params });

export const getFlatCategoriesTrace = (category: Category, result: Category[] = []): Category[] => {
  // Check if this is an Apple category - if so, use special Apple logic
  if (isAppleCategory(category)) {
    return getAppleCategoriesTrace(category, result);
  }

  // Original logic for non-Apple categories
  result.push({
    id: category.id,
    name: category.name,
    pId: category.pId,
  });

  if (category.subCategories) {
    for (const subCategory of category.subCategories) {
      getFlatCategoriesTrace(subCategory, result);
    }
  }
  return result;
};

export const getAppleCategoriesTrace = (category: Category, result: Category[] = []): Category[] => {
  if (category.id !== APPLE_CATEGORY_ID) {
    result.push({
      id: category.id,
      name: category.name,
      pId: category.pId,
    });
  }
  return result;
};

export const processAppleCategoryPath = (categoryPath: Category[]): Category[] => {
  // Filter out the Apple root category (60001) since we add "Apple" as a brand segment
  const filteredPath = categoryPath
    .filter((category) => category.id !== APPLE_CATEGORY_ID)
    .map((category) => ({
      id: category.id,
      name: category.name,
      pId: category.pId,
    }));

  // Limit to 2 subcategories deep below Apple
  // This means: Apple → Main Category → 1st Subcategory (max 2 levels)
  return filteredPath.slice(0, 2);
};
